// src/components/enrollments/EnrollStudentModal.jsx
import React, { useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import "./enrollStudentModal.css";

const EnrollStudentModal = ({
  isOpen,
  onClose,
  student,
  courseKeys = [],
  courseMeta = [],
  scheduleByCourseKey = {},
  modelTimeSlotsByCourseKey = {},
  onChange,
  titleOverride,
  onSave,
}) => {
  const keys = useMemo(
    () => (Array.isArray(courseKeys) ? courseKeys.slice(0, 3) : []),
    [courseKeys]
  );

  const courseList = useMemo(() => {
    const metaByKey = {};
    (courseMeta || []).forEach((c) => {
      if (!c) return;
      const k = c.key || c.courseKey;
      if (!k) return;
      metaByKey[k] = c;
    });

    return keys.map((k) => {
      const meta = metaByKey[k] || { key: k, title: k.replace(/_/g, " "), track: "Course" };
      const slots = modelTimeSlotsByCourseKey?.[k] || [];
      const requiresModel = Array.isArray(slots) && slots.length > 0;

      const sched = scheduleByCourseKey?.[k] || {};
      const startDate = sched.startDate || "";
      const modelTimeSlot = sched.modelTimeSlot || (requiresModel ? slots[0] : "");

      return {
        key: k,
        title: meta.title || meta.name || k.replace(/_/g, " "),
        track: meta.track || "",
        requiresModel,
        slots,
        startDate,
        modelTimeSlot,
      };
    });
  }, [keys, courseMeta, scheduleByCourseKey, modelTimeSlotsByCourseKey]);

  useEffect(() => {
    if (typeof onChange !== "function") return;

    courseList.forEach((c) => {
      if (!c.requiresModel) return;
      const existing = scheduleByCourseKey?.[c.key]?.modelTimeSlot || "";
      const defaultSlot = c.slots?.[0] || "";
      if (!existing && defaultSlot) onChange(c.key, { modelTimeSlot: defaultSlot });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseList]);

  const studentName = useMemo(() => {
    if (!student) return "Student";
    const first = student.firstName || student.first_name || "";
    const last = student.lastName || student.last_name || "";
    const full = `${first} ${last}`.trim();
    return full || student.email || "Student";
  }, [student]);

  const isValid = useMemo(() => {
    if (!courseList.length) return false;
    for (const c of courseList) {
      if (!c.startDate) return false;
      if (c.requiresModel && !c.modelTimeSlot) return false;
    }
    return true;
  }, [courseList]);

  const handleSave = async () => {
    if (typeof onSave === "function") {
      await onSave();
      return;
    }
    onClose && onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [isOpen]);

  if (!isOpen) return null;

  const modal = (
    <div className="enroll-modal-overlay" onClick={onClose}>
      <div className="enroll-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="enroll-modal-head">
          <div className="enroll-modal-title">{titleOverride || `Set class dates + model times`}</div>
          <button type="button" className="enroll-modal-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="enroll-modal-sub">
          Assign the Day 1 date for each selected course
          {courseList.some((c) => c.requiresModel) ? " and choose the model time slot where required." : "."}
        </div>

        <div className="enroll-modal-student">
          <span className="enroll-modal-student-label">Student:</span>
          <span className="enroll-modal-student-name">{studentName}</span>
        </div>

        {!courseList.length ? (
          <div className="enroll-modal-empty">No courses selected. Close this window and select up to 3 courses first.</div>
        ) : (
          <div className="enroll-modal-body">
            {courseList.map((course) => {
              const { key, title, track, requiresModel, slots, startDate, modelTimeSlot } = course;

              return (
                <div key={key} className="enroll-modal-row">
                  <div className="enroll-modal-course">
                    <div className="enroll-modal-course-title">{title}</div>
                    {track ? <div className="enroll-modal-course-track">{track}</div> : null}
                  </div>

                  <div className="enroll-modal-fields">
                    <label className="enroll-modal-field">
                      <span className="enroll-modal-label">
                        Day 1 date <span className="enroll-modal-required">*</span>
                      </span>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => onChange && onChange(key, { startDate: e.target.value })}
                        className="enroll-modal-input"
                      />
                    </label>

                    {requiresModel ? (
                      <label className="enroll-modal-field">
                        <span className="enroll-modal-label">
                          Model time <span className="enroll-modal-required">*</span>
                        </span>
                        <select
                          value={modelTimeSlot}
                          onChange={(e) => onChange && onChange(key, { modelTimeSlot: e.target.value })}
                          className="enroll-modal-input"
                        >
                          {slots.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                        <div className="enroll-modal-hint">
                          This course requires a model on a later day (the system calculates the model date automatically).
                        </div>
                      </label>
                    ) : (
                      <div className="enroll-modal-field enroll-modal-field-muted">
                        <span className="enroll-modal-label">Model time</span>
                        <div className="enroll-modal-muted">Not required for this course.</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="enroll-modal-actions">
          <button type="button" className="enroll-modal-btn enroll-modal-btn-secondary" onClick={onClose}>
            Cancel
          </button>

          <button
            type="button"
            className="enroll-modal-btn"
            disabled={!isValid}
            onClick={handleSave}
            title={!isValid ? "Set required dates/times first." : "Save"}
            style={{ opacity: !isValid ? 0.6 : 1 }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default EnrollStudentModal;
